import { useEffect } from 'react';
import { CONSENT_CHANGED_EVENT, hasConsent } from '../../utils/cookieConsent';

/**
 * ZendeskWidget Component
 * Initializes and manages the Zendesk Messaging widget (new API)
 *
 * The new Zendesk Messaging widget uses:
 *   zE('messenger', 'show')
 *   zE('messenger', 'hide')
 *   zE('messenger', 'open')
 *   zE('messenger', 'close')
 *
 * NOT the old Web Widget Classic API:
 *   zE('webWidget', 'show')  ← this throws "Method does not exist"
 */
const ZendeskWidget = () => {
  useEffect(() => {
    const loadWidget = () => {
      if (!hasConsent('marketing')) return;

      const widgetKey = process.env.REACT_APP_ZENDESK_WIDGET_KEY;

      if (!widgetKey) {
        console.warn('⚠️ Zendesk: REACT_APP_ZENDESK_WIDGET_KEY not set');
        return;
      }

      const isValidKey = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/.test(widgetKey);
      if (!isValidKey) {
        console.warn('⚠️ Zendesk: Widget key format invalid:', widgetKey);
        return;
      }

      if (document.getElementById('ze-snippet')) {
        bindGlobalHelpers();
        return;
      }

      const script = document.createElement('script');
      script.id = 'ze-snippet';
      script.src = `https://static.zdassets.com/ekr/snippet.js?key=${widgetKey}`;
      script.async = true;

      script.onload = () => {
        console.log('✓ Zendesk script loaded');
        bindGlobalHelpers();
      };

      script.onerror = () => {
        console.error('❌ Zendesk script failed to load');
      };

      document.head.appendChild(script);
    };

    loadWidget();
    window.addEventListener(CONSENT_CHANGED_EVENT, loadWidget);
    return () => {
      window.removeEventListener(CONSENT_CHANGED_EVENT, loadWidget);
    };
  }, []);

  return null;
};

/**
 * Bind window helpers using the correct Messaging API.
 * Called after the script loads (or immediately if already loaded).
 */
function bindGlobalHelpers() {
  // open chat
  window.openZendeskChat = () => {
    try {
      if (typeof window.zE === 'function') {
        window.zE('messenger', 'open');
      }
    } catch (e) {
      console.warn('Zendesk open error:', e.message);
      window.location.href = '/contact-us';
    }
  };

  // hide the launcher bubble
  window.hideZendeskWidget = () => {
    try {
      if (typeof window.zE === 'function') {
        window.zE('messenger', 'hide');
      }
    } catch (e) {
      console.warn('Zendesk hide error:', e.message);
    }
  };

  // show the launcher bubble
  window.showZendeskWidget = () => {
    try {
      if (typeof window.zE === 'function') {
        window.zE('messenger', 'show');
      }
    } catch (e) {
      console.warn('Zendesk show error:', e.message);
    }
  };
}

export default ZendeskWidget;
